/**
 * Typewriter Effect JavaScript
 * Zie: ARCHITECTUUR.md - Typography Effects
 * 
 * Twee modi:
 * 1. CSS-based: data-typewriter attribute - simpele reveal animatie
 * 2. JS-based: data-typewriter-chars attribute - karakter voor karakter
 */

(function() {
  'use strict';

  // ==========================================================================
  // CSS-based Typewriter (width animation)
  // ==========================================================================
  
  class TypewriterCSS {
    constructor(element) {
      this.element = element;
      this.text = element.textContent.trim();
      this.baseSpeed = parseInt(element.dataset.typewriterSpeed) || 80; // ms per char (slower, more human)
      this.delay = parseFloat(element.dataset.typewriterDelay) || 0;
      this.triggered = false;
      this.currentIndex = 0;
      
      this.init();
    }
    
    // Human-like timing with natural variation
    getHumanDelay() {
      // Base delay with random variation (±40%)
      let delay = this.baseSpeed * (0.6 + Math.random() * 0.8);
      
      // Pause longer after punctuation
      const lastChar = this.text[this.currentIndex - 1];
      if (lastChar && '.!?'.includes(lastChar)) {
        delay += 400 + Math.random() * 200;
      } else if (lastChar && ',;:'.includes(lastChar)) {
        delay += 150 + Math.random() * 100;
      } else if (lastChar === ' ') {
        delay += 30 + Math.random() * 50;
      }
      
      return delay;
    }
    
    init() {
      // Respecteer 'verminderde beweging': laat de kop gewoon staan i.p.v. hem leeg te maken
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.element.classList.add('typewriter-done');
        return;
      }

      // Create container for character-by-character typing
      this.container = document.createElement('span');
      this.container.className = 'typewriter-container';
      
      // Create cursor element
      this.cursor = document.createElement('span');
      this.cursor.className = 'typewriter-cursor';
      this.cursor.textContent = '|';
      
      this.element.textContent = '';
      this.element.appendChild(this.container);
      this.element.appendChild(this.cursor);
      this.element.classList.add('typewriter-ready');
      
      // Setup intersection observer
      this.setupObserver();
    }
    
    setupObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.triggered) {
            this.triggered = true;
            setTimeout(() => this.start(), this.delay * 1000);
            observer.disconnect();
          }
        });
      }, { threshold: 0.5 });
      
      observer.observe(this.element);
    }
    
    start() {
      this.element.classList.add('typewriter-active');
      this.typeNextChar();
    }
    
    typeNextChar() {
      if (this.currentIndex >= this.text.length) {
        // Typing complete - hide cursor after brief pause
        setTimeout(() => {
          this.cursor.classList.add('typewriter-cursor--hidden');
          this.element.classList.remove('typewriter-active');
          this.element.classList.add('typewriter-complete');
        }, 500);
        return;
      }
      
      // Add next character
      const char = this.text[this.currentIndex];
      this.container.textContent += char;
      this.currentIndex++;
      
      // Schedule next character with human-like delay
      setTimeout(() => this.typeNextChar(), this.getHumanDelay());
    }
  }

  // ==========================================================================
  // JS-based Typewriter (character by character)
  // ==========================================================================
  
  class TypewriterChars {
    constructor(element) {
      this.element = element;
      this.text = element.textContent.trim();
      this.speed = parseInt(element.dataset.typewriterSpeed) || 50; // ms per char
      this.delay = parseFloat(element.dataset.typewriterDelay) || 0;
      this.triggered = false;
      this.currentIndex = 0;
      
      this.init();
    }
    
    init() {
      // Create character spans
      this.element.textContent = '';
      this.chars = [];
      
      for (let i = 0; i < this.text.length; i++) {
        const charSpan = document.createElement('span');
        charSpan.className = 'typewriter-char';
        charSpan.textContent = this.text[i] === ' ' ? '\u00A0' : this.text[i];
        this.element.appendChild(charSpan);
        this.chars.push(charSpan);
      }
      
      this.element.classList.add('typewriter-ready');
      
      // Setup intersection observer
      this.setupObserver();
    }
    
    setupObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.triggered) {
            this.triggered = true;
            setTimeout(() => this.start(), this.delay * 1000);
            observer.disconnect();
          }
        });
      }, { threshold: 0.5 });
      
      observer.observe(this.element);
    }
    
    start() {
      this.typeNextChar();
    }
    
    typeNextChar() {
      if (this.currentIndex >= this.chars.length) {
        this.element.classList.add('typewriter-complete');
        return;
      }
      
      const char = this.chars[this.currentIndex];
      char.classList.add('visible', 'current');
      
      // Remove 'current' class from previous char
      if (this.currentIndex > 0) {
        this.chars[this.currentIndex - 1].classList.remove('current');
      }
      
      this.currentIndex++;
      setTimeout(() => this.typeNextChar(), this.speed);
    }
  }

  // ==========================================================================
  // Auto-initialize on DOM ready
  // ==========================================================================
  
  function initTypewriters() {
    // CSS-based typewriters
    document.querySelectorAll('[data-typewriter]').forEach(el => {
      new TypewriterCSS(el);
    });
    
    // Character-based typewriters
    document.querySelectorAll('[data-typewriter-chars]').forEach(el => {
      new TypewriterChars(el);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTypewriters);
  } else {
    initTypewriters();
  }

  // Expose for dynamic content
  window.PRNTS = window.PRNTS || {};
  window.PRNTS.TypewriterCSS = TypewriterCSS;
  window.PRNTS.TypewriterChars = TypewriterChars;
  window.PRNTS.initTypewriters = initTypewriters;

})();
